import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { format, subDays, subMonths, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface BudgetAnalytics {
  overview: {
    totalBudgets: number;
    totalValue: number;
    conversionRate: number;
    averageTicket: number;
    growth: {
      budgets: number;
      value: number;
      conversion: number;
    };
  };
  statusDistribution: Array<{
    status: string;
    count: number;
    value: number;
    percentage: number;
  }>;
  monthlyData: Array<{
    month: string;
    budgets: number;
    value: number;
    conversion: number;
    approved: number;
    rejected: number;
    pending: number;
  }>;
  topServices: Array<{
    name: string;
    budgets: number;
    value: number;
    avgTicket: number;
  }>;
  salesPerformance: Array<{
    seller: string;
    budgets: number;
    value: number;
    conversion: number;
    rank: number;
  }>;
  conversionMetrics: {
    funnelData: Array<{
      stage: string;
      count: number;
      value: number;
      conversionRate: number;
    }>;
    averageClosingTime: number;
    followUpRate: number;
    contactsPerBudget: number;
  };
}

export interface AnalyticsFilters {
  startDate: string;
  endDate: string;
  clientId?: string;
  categoryId?: string;
  userId?: string;
  minValue?: number;
  maxValue?: number;
}

const formatDateForAPI = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

export function useBudgetAnalytics(filters: Partial<AnalyticsFilters> = {}) {
  const [data, setData] = useState<BudgetAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultEndDate = endOfDay(new Date());
  const defaultStartDate = subMonths(defaultEndDate, 6);

  const effectiveFilters = {
    startDate: filters.startDate || formatDateForAPI(defaultStartDate),
    endDate: filters.endDate || formatDateForAPI(defaultEndDate),
    clientId: filters.clientId,
    categoryId: filters.categoryId,
    userId: filters.userId,
    minValue: filters.minValue,
    maxValue: filters.maxValue,
  };

  const fetchOverviewData = useCallback(async () => {
    try {
      const { data: overviewData, error: overviewError } = await supabase.rpc('get_budget_summary', {
        p_start_date: effectiveFilters.startDate,
        p_end_date: effectiveFilters.endDate,
        p_client_id: effectiveFilters.clientId,
        p_category_id: effectiveFilters.categoryId,
        p_user_id: effectiveFilters.userId,
      });

      if (overviewError) throw overviewError;

      // Calculate previous period for comparison
      const previousPeriodStart = formatDateForAPI(subDays(new Date(effectiveFilters.startDate), 180));
      const previousPeriodEnd = formatDateForAPI(subDays(new Date(effectiveFilters.startDate), 1));

      const { data: previousData, error: previousError } = await supabase.rpc('get_budget_summary', {
        p_start_date: previousPeriodStart,
        p_end_date: previousPeriodEnd,
        p_client_id: effectiveFilters.clientId,
        p_category_id: effectiveFilters.categoryId,
        p_user_id: effectiveFilters.userId,
      });

      if (previousError) throw previousError;

      return {
        current: overviewData?.[0] || {},
        previous: previousData?.[0] || {},
      };
    } catch (err) {
      console.error('Error fetching overview data:', err);
      return null;
    }
  }, [effectiveFilters]);

  const fetchConversionRate = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_budget_conversion_rate', {
        p_start_date: effectiveFilters.startDate,
        p_end_date: effectiveFilters.endDate,
        p_group_by: 'month',
        p_client_id: effectiveFilters.clientId,
        p_user_id: effectiveFilters.userId,
      });

      if (error) throw error;

      return data || [];
    } catch (err) {
      console.error('Error fetching conversion rate:', err);
      return [];
    }
  }, [effectiveFilters]);

  const fetchMonthlyData = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_budgets_by_month', {
        p_start_date: effectiveFilters.startDate,
        p_end_date: effectiveFilters.endDate,
        p_client_id: effectiveFilters.clientId,
        p_user_id: effectiveFilters.userId,
      });

      if (error) throw error;

      return data || [];
    } catch (err) {
      console.error('Error fetching monthly data:', err);
      return [];
    }
  }, [effectiveFilters]);

  const fetchTopServices = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_budget_items_report', {
        p_start_date: effectiveFilters.startDate,
        p_end_date: effectiveFilters.endDate,
        p_group_by: 'service',
        p_limit: 10,
      });

      if (error) throw error;

      return data || [];
    } catch (err) {
      console.error('Error fetching top services:', err);
      return [];
    }
  }, [effectiveFilters]);

  const fetchTopClients = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_top_budget_clients', {
        p_start_date: effectiveFilters.startDate,
        p_end_date: effectiveFilters.endDate,
        p_limit: 10,
        p_order_by: 'value',
      });

      if (error) throw error;

      return data || [];
    } catch (err) {
      console.error('Error fetching top clients:', err);
      return [];
    }
  }, [effectiveFilters]);

  const fetchPerformanceData = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_budget_performance', {
        p_start_date: effectiveFilters.startDate,
        p_end_date: effectiveFilters.endDate,
        p_group_by: 'user',
        p_limit: 10,
      });

      if (error) throw error;

      return data || [];
    } catch (err) {
      console.error('Error fetching performance data:', err);
      return [];
    }
  }, [effectiveFilters]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        overview,
        conversionData,
        monthlyData,
        topServices,
        topClients,
        performanceData,
      ] = await Promise.all([
        fetchOverviewData(),
        fetchConversionRate(),
        fetchMonthlyData(),
        fetchTopServices(),
        fetchTopClients(),
        fetchPerformanceData(),
      ]);

      if (!overview) {
        throw new Error('Failed to fetch overview data');
      }

      // Calculate growth rates
      const calculateGrowth = (current: number, previous: number) => {
        if (previous === 0) return 0;
        return ((current - previous) / previous) * 100;
      };

      // Process status distribution
      const statusDistribution = [
        {
          status: 'draft',
          count: overview.current.draft_count || 0,
          value: overview.current.draft_value || 0,
          percentage: 0,
        },
        {
          status: 'sent',
          count: overview.current.sent_count || 0,
          value: overview.current.sent_value || 0,
          percentage: 0,
        },
        {
          status: 'approved',
          count: overview.current.approved_count || 0,
          value: overview.current.approved_value || 0,
          percentage: 0,
        },
        {
          status: 'rejected',
          count: overview.current.rejected_count || 0,
          value: overview.current.rejected_value || 0,
          percentage: 0,
        },
        {
          status: 'expired',
          count: overview.current.expired_count || 0,
          value: overview.current.expired_value || 0,
          percentage: 0,
        },
      ];

      const totalCount = statusDistribution.reduce((sum, status) => sum + status.count, 0);
      statusDistribution.forEach(status => {
        status.percentage = totalCount > 0 ? (status.count / totalCount) * 100 : 0;
      });

      // Process monthly data with Portuguese month names
      const processedMonthlyData = monthlyData.map(item => ({
        month: format(new Date(item.month + '-01'), 'MMM', { locale: ptBR }),
        budgets: item.total_budgets || 0,
        value: item.total_value || 0,
        conversion: item.conversion_rate || 0,
        approved: item.approved_count || 0,
        rejected: item.rejected_count || 0,
        pending: item.sent_count || 0,
      }));

      // Process top services
      const processedTopServices = topServices.map((service, index) => ({
        name: service.service_name || `Serviço ${index + 1}`,
        budgets: service.budget_count || 0,
        value: service.total_value || 0,
        avgTicket: service.average_value || 0,
      }));

      // Process performance data (using top clients as seller performance for now)
      const processedPerformance = performanceData.map((perf, index) => ({
        seller: perf.user_name || perf.client_name || `Vendedor ${index + 1}`,
        budgets: perf.budget_count || 0,
        value: perf.total_value || 0,
        conversion: perf.conversion_rate || 0,
        rank: index + 1,
      }));

      // Calculate conversion metrics
      const totalBudgets = overview.current.total_count || 0;
      const approvedBudgets = overview.current.approved_count || 0;
      const sentBudgets = overview.current.sent_count || 0;

      const conversionMetrics = {
        funnelData: [
          {
            stage: 'Rascunho',
            count: overview.current.draft_count || 0,
            value: overview.current.draft_value || 0,
            conversionRate: 100,
          },
          {
            stage: 'Enviado',
            count: sentBudgets,
            value: overview.current.sent_value || 0,
            conversionRate: totalBudgets > 0 ? (sentBudgets / totalBudgets) * 100 : 0,
          },
          {
            stage: 'Aprovado',
            count: approvedBudgets,
            value: overview.current.approved_value || 0,
            conversionRate: sentBudgets > 0 ? (approvedBudgets / sentBudgets) * 100 : 0,
          },
        ],
        averageClosingTime: 15, // Mock data - would need additional RPC function
        followUpRate: 25, // Mock data - would need additional RPC function
        contactsPerBudget: 3.2, // Mock data - would need additional RPC function
      };

      const analyticsData: BudgetAnalytics = {
        overview: {
          totalBudgets: overview.current.total_count || 0,
          totalValue: overview.current.total_value || 0,
          conversionRate: overview.current.conversion_rate || 0,
          averageTicket: overview.current.average_value || 0,
          growth: {
            budgets: calculateGrowth(overview.current.total_count || 0, overview.previous.total_count || 0),
            value: calculateGrowth(overview.current.total_value || 0, overview.previous.total_value || 0),
            conversion: calculateGrowth(overview.current.conversion_rate || 0, overview.previous.conversion_rate || 0),
          },
        },
        statusDistribution,
        monthlyData: processedMonthlyData,
        topServices: processedTopServices,
        salesPerformance: processedPerformance,
        conversionMetrics,
      };

      setData(analyticsData);
    } catch (err) {
      console.error('Error fetching budget analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  }, [
    fetchOverviewData,
    fetchConversionRate,
    fetchMonthlyData,
    fetchTopServices,
    fetchTopClients,
    fetchPerformanceData,
  ]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const refetch = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    data,
    loading,
    error,
    refetch,
    filters: effectiveFilters,
  };
}